import React, { useState, useMemo, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@pwc/pwc-deals-ui';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { logOutThunk } from 'common/redux/security/LogOut/logOutThunk';
import { getFooterThunk } from 'common/redux/termsAndConditions/getFooterThunk';
import { setUserAcceptedTermsHasAccepted } from 'common/services/userTermsAndConditionsService';
import authorizationService from 'common/services/security/authorizationService';
import {
  setUserTermsAndConditionsReceived,
  getUserTermsAndConditionsHasAccepted,
  getUserTermsAndConditionsLoaded,
} from 'common/redux/userTermsAndConditions/userTermsAndConditionsSlice';
import {
  getTermsAndConditions,
  getTermsAndConditionsLoading,
} from 'common/redux/termsAndConditions/termsAndConditionsSlice';
import { getTermsAndConditionsThunk } from 'common/redux/termsAndConditions/getTermsAndConditionsThunk';
import Policy from './components/Policy/Policy';
import './TermsAndConditions.scss';

const TermsAndConditions = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [disabled, setDisabled] = useState(true);
  const tAndCs = useSelector(getTermsAndConditions);
  const tAndCsLoading = useSelector(getTermsAndConditionsLoading);
  const userTAndCsAccepted = useSelector(getUserTermsAndConditionsHasAccepted);
  const userTAndCsLoaded = useSelector(getUserTermsAndConditionsLoaded);

  useEffect(() => {
    if (userTAndCsAccepted) {
      history.replace('/projects');
    }
  }, [userTAndCsAccepted, history]);

  useEffect(() => {
    if (!userTAndCsLoaded) {
      history.replace('/');
    }
  }, [userTAndCsLoaded, history]);

  useEffect(() => {
    if (!tAndCs && !tAndCsLoading) {
      dispatch(getTermsAndConditionsThunk());
      dispatch(getFooterThunk());
    }
  }, [tAndCs, tAndCsLoading, dispatch]);

  const handleTextScroll = useMemo(() => {
    return (e) => {
      if (
        e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight <=
        5
      ) {
        setDisabled(false);
      }
    };
  }, []);

  const handleAccept = async () => {
    try {
      await setUserAcceptedTermsHasAccepted(authorizationService.getEmail());
    } catch (_) {
      return;
    }
    dispatch(setUserTermsAndConditionsReceived(true));
    history.push('/projects');
  };

  return (
    <div
      data-testid="terms-and-conditions"
      className="terms-and-conditions px-3 px-md-5 pt-5"
    >
      <h1>{t('legalAgreement')}</h1>
      <div
        data-testid="terms-and-conditions-text"
        className="terms-and-conditions-text scrollable"
        onScroll={handleTextScroll}
      >
        <p className="terms-and-conditions-instructions">
          {t('beforeYouCanUsePwCJunction')}
        </p>
        {tAndCs &&
          tAndCs.map((tAndC) => {
            if (tAndC.title !== 'Copyright') {
              return (
                <Fragment key={tAndC.id}>
                  <hr />
                  <Policy policy={tAndC} />
                </Fragment>
              );
            }

            return null;
          })}

        <div className="terms-and-conditions-footer-fade" />
      </div>
      <div
        className="terms-and-conditions-legal-statement"
        data-testid="terms-and-conditions-legal-statement"
      >
        By clicking the button below, you are accepting the Terms of Use and any
        applicable territory-specific terms, and acknowledging that you have
        read and understand the Privacy Statement and Cookie Statement.
      </div>
      <div className="terms-and-conditions-actions">
        <Button
          data-testid="cancel-terms-and-conditions"
          onClick={() => {
            dispatch(logOutThunk());
          }}
          type="button"
          className="button-link mr-4"
        >
          {t('cancelBtn')}
        </Button>
        <Button
          data-testid="accept-terms-and-conditions"
          onClick={handleAccept}
          type="button"
          className="button"
          disabled={disabled}
        >
          {t('agreeAndProceed')}
        </Button>
      </div>
    </div>
  );
};

export default TermsAndConditions;
